import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, House, Sparkles, Palette } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import { WORK } from "../data/content";
import { scrollToSection } from "../utils/scroll";

const ease = [0.22, 1, 0.36, 1];
const ICONS = { home: House, sparkles: Sparkles, palette: Palette };

function StatusChip({ status }) {
  const done = status === "Done";
  return (
    <span
      className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] ${
        done ? "bg-emerald-500/15 text-emerald-600" : "bg-[#D4AF37]/15 text-[#B59328]"
      }`}
    >
      {status}
    </span>
  );
}

function WorkCard({ item, index }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = item.icon ? ICONS[item.icon] : null;
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.65, delay: 0.08 * (index % 3), ease }}
      whileHover={{ y: -10 }}
      className="group flex h-full flex-col overflow-hidden rounded-3xl bg-white shadow-[0_10px_30px_rgba(13,75,86,0.06)] transition-shadow duration-300 hover:shadow-[0_24px_50px_rgba(13,75,86,0.15)]"
      data-testid={`work-card-${item.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      {item.image ? (
        <div className="relative h-52 overflow-hidden">
          <img
            src={item.image}
            alt={item.alt}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute left-4 top-4 flex items-center gap-2">
            <span className="rounded-full bg-[#0D4B56]/85 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#F3E096] backdrop-blur">
              Real impact · Punjab floods
            </span>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#08353D]/45 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
        </div>
      ) : (
        <div className="relative flex h-52 items-center justify-center overflow-hidden bg-gradient-to-br from-[#0D4B56] to-[#08353D]">
          <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#D4AF37]/15 blur-2xl" />
          {Icon && (
            <span className="flex h-20 w-20 items-center justify-center rounded-3xl bg-white/10 text-[#F3E096] transition-transform duration-500 group-hover:scale-110">
              <Icon size={40} />
            </span>
          )}
        </div>
      )}
      <div className="flex flex-1 flex-col p-6">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-xl font-bold text-[#0F172A]">{item.title}</h3>
          <StatusChip status={item.status} />
        </div>
        <p className="mt-2 text-sm leading-relaxed text-[#475569]">{item.desc}</p>
        <div
          className={`grid transition-all duration-500 ease-out ${
            expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0 group-hover:opacity-100 sm:group-hover:grid-rows-[1fr]"
          }`}
        >
          <div className="overflow-hidden">
            <p className="pt-3 text-sm leading-relaxed text-[#B59328]">{item.more}</p>
          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          data-testid={`work-learn-more-${index}`}
          className="mt-5 inline-flex items-center gap-2 self-start rounded-full bg-[#E6F0F2] px-5 py-2.5 text-sm font-bold text-[#0D4B56] transition-all duration-300 hover:bg-[#D4AF37] hover:text-[#08353D]"
        >
          {expanded ? "Show Less" : "Learn More"}
          <ArrowRight size={16} className={`transition-transform duration-300 ${expanded ? "rotate-90" : "group-hover:translate-x-1"}`} />
        </button>
      </div>
    </motion.article>
  );
}

export default function Work() {
  return (
    <section id="work" className="bg-sanddark py-16 sm:py-24 lg:py-32" data-testid="work-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <SectionHeading
            eyebrow="What We Do"
            title="Our work so far —"
            accent="and what's next."
            sub="Everything below actually happened, or is genuinely on its way. Every effort planned, packed and delivered personally by our team."
          />
          <motion.a
            href="#get-involved"
            onClick={(e) => scrollToSection(e, "#get-involved")}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2, ease }}
            className="hidden shrink-0 items-center gap-2 rounded-full border-2 border-[#0D4B56] px-7 py-3 text-sm font-bold text-[#0D4B56] transition-all duration-300 hover:bg-[#0D4B56] hover:text-[#F3E096] md:inline-flex"
          >
            Join a drive <ArrowRight size={16} />
          </motion.a>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {WORK.map((item, i) => (
            <WorkCard key={item.title} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
